import { of } from 'rxjs';

import {
  withLatestFrom,
  throttleTime,
  exhaustMap,
  filter,
  switchMap,
  share,
  mergeMap,
  catchError,
  map,
} from 'rxjs/operators';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { SuitabilityAction } from './suitability.action';
import { BackendService } from '../../services/backend/backend.service';
import { Injectable } from '@angular/core';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { ErrorsType } from '../../enum/errors.type';
import { RoutesTable } from '../../resources/routes-table.model';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';

@Injectable()
export class SuitabilityEffects {
  /**
   * Lista de respostas de suitability
   */
  @Effect() public getAnswers$ = this.actions$.pipe(
    ofType(SuitabilityAction.getAnswers),
    switchMap((action) => {
      return this.backendService
        .get<DefaultResponse>(RoutesTable.suitability.routes.getAnswers.uri)
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.getAnswersSuccess,
                payload: result.value,
              };
            }

            if (result.hasAlerta) {
              return {
                type: SuitabilityAction.getAnswersError,
                payload: result.messages,
              };
            }
            return {
              type: SuitabilityAction.getAnswersError,
              payload: result.messages,
            };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.getAnswersError, payload: error }),
          ),
        );
    }),
    share(),
  );

  /**
   * Lista de perguntas de suitability
   */
  @Effect() public getQuestions$ = this.actions$.pipe(
    ofType(SuitabilityAction.getQuestions),
    withLatestFrom(this.store.select((state) => state.suitability)),
    filter(([action, state]) => state.questions.length === 0),
    switchMap(([action, state]) => {
      return this.backendService
        .get<DefaultResponse>(RoutesTable.suitability.routes.getQuestions.uri)
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.getQuestionsSuccess,
                payload: result.value,
              };
            }

            if (result.hasAlerta) {
              return {
                type: SuitabilityAction.getQuestionsError,
                payload: result.messages,
              };
            }

            return {
              type: SuitabilityAction.getQuestionsError,
              payload: result.messages,
            };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.getQuestionsError, payload: error }),
          ),
        );
    }),
    share(),
  );

  /**
   * Obtém uma questão de suitability
   */
  @Effect() public getQuestion$ = this.actions$.pipe(
    ofType(SuitabilityAction.getQuestion),
    switchMap((action: any) => {
      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.suitability.routes.getQuestion,
            action.payload,
          ),
        )
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.getQuestionSuccess,
                payload: result.value,
              };
            }

            if (result.hasAlerta) {
              return {
                type: SuitabilityAction.getQuestionError,
                payload: result.messages,
              };
            }

            return {
              type: SuitabilityAction.getQuestionError,
              payload: result.messages,
            };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.getQuestionError, payload: error }),
          ),
        );
    }),
    share(),
  );

  /**
   * Obtém as perguntas e respostas de suitability do usuário
   */
  @Effect() public getUserAnswers$ = this.actions$.pipe(
    ofType(SuitabilityAction.getUserAnswers),
    switchMap((action: any) => {
      return this.backendService
        .get<DefaultResponse>(RoutesTable.suitability.routes.getUserAnswers.uri)
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.getUserAnswersSuccess,
                payload: result.value,
              };
            }

            if (result.hasValue) {
              return {
                type: SuitabilityAction.getUserAnswersError,
                payload: result.messages,
              };
            }

            return {
              type: SuitabilityAction.getUserAnswersError,
              payload: result.messages,
            };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.getUserAnswersError, payload: error }),
          ),
        );
    }),
    share(),
  );

  /**
   * Obtém o suitability do usuário
   */
  @Effect() public getProfile$ = this.actions$.pipe(
    ofType(SuitabilityAction.getProfile),
    withLatestFrom(this.store.select((state) => state.authentication)),
    filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
    throttleTime(5000),
    exhaustMap((action) => {
      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.suitability.routes.getUserSuitability.uri,
        )
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.getProfileSuccess,
                payload: result.value,
              };
            }
            return {
              type: SuitabilityAction.getProfileError,
              payload: result.messages,
            };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.getProfileError, payload: error }),
          ),
        );
    }),
    share(),
  );

  /**
   * Auxiliar para ajudar nos passos do suitability
   */
  @Effect() public answerStep$ = this.actions$.pipe(
    ofType(SuitabilityAction.answerStep),
    mergeMap((action: any) => {
      return of({
        type: SuitabilityAction.answerStepSuccess,
        payload: action.payload,
      });
    }),
    share(),
  );

  /**
   * Insere perguntas e respostas de um usuário sem definir um suitability
   */
  @Effect() public updateUserAnswers$ = this.actions$.pipe(
    ofType(SuitabilityAction.updateUserAnswers),
    mergeMap((action: any) => {
      return this.backendService
        .post<DefaultResponse>(
          RoutesTable.suitability.routes.updateUserAnswers.uri,
          action.payload,
          action.token,
        )
        .pipe(
          map((result: any) => {
            if (result.hasAlerta) {
              return {
                type: SuitabilityAction.updateUserAnswersError,
                payload: { type: ErrorsType.Warning, value: result.messages },
              };
            }

            if (result.hasError) {
              return {
                type: SuitabilityAction.updateUserAnswersError,
                payload: { type: ErrorsType.Error, value: result.messages },
              };
            }

            if (result.ok && result.hasValue) {
              return {
                type: SuitabilityAction.updateUserAnswersSuccess,
                payload: result,
              };
            }

            return {
              type: SuitabilityAction.updateUserAnswersSuccess,
              payload: result,
            };
          }),
          catchError((error) =>
            of({
              type: SuitabilityAction.updateUserAnswersError,
              payload: error,
            }),
          ),
        );
    }),
    share(),
  );

  /**
   * Insere perguntas e respostas de um usuário para gerar um guided sale.
   */
  @Effect() public guidedSale$ = this.actions$.pipe(
    ofType(SuitabilityAction.guidedSale),
    mergeMap((action: any) => {
      return this.backendService
        .post<DefaultResponse>(
          RoutesTable.suitability.routes.setGuidedsale.uri,
          action.payload,
        )
        .pipe(
          map((result: any) => {
            if (result.ok) {
              return {
                type: SuitabilityAction.guidedSaleSuccess,
                payload: result.value,
              };
            }

            return { type: SuitabilityAction.guidedSaleError, payload: result };
          }),
          catchError((error) =>
            of({ type: SuitabilityAction.guidedSaleError, payload: error }),
          ),
        );
    }),
    share(),
  );

  constructor(
    private actions$: Actions<SuitabilityAction>,
    private backendService: BackendService,
    private store: Store<AppState>,
  ) {}
}
